import { rowsToSavePayload } from './demPoMapping';
import { sendPendingOrderNotifications, type PendingNotificationResult } from './demOrderNotifications';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import { markWhatsAppQueueProcessed } from './demWhatsAppQueue';
import { assertDemActorReady } from './demUserNames';
import type { DemPoExtractHeader, DemPoReviewRow } from '../types/dem';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function parseDeliveryDateForDb(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return null;
}

function parseDeliveryDateDisplay(dbDate: string): string {
  const [y, m, d] = dbDate.split('-');
  if (!y || !m || !d) return dbDate;
  return `${d}/${m}/${y}`;
}

async function getNextWaNumber(): Promise<string> {
  const db = getDbSupabase('dem');
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data, error } = await db.rpc('generate_wa_number');
    if (error) throw new Error(formatSupabaseError(error));
    const wa = String(data ?? '').trim();
    if (wa && wa.toLowerCase() !== 'none') return wa;
  }
  throw new Error('Failed to generate WhatsApp order number from server.');
}

export function previewWaOrderNumber(): string {
  const now = new Date();
  const period = `${String(now.getFullYear()).slice(-2)}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return `WA${period}-?????`;
}

export type SaveWaOrderResult = {
  order_id: string;
  notifications: PendingNotificationResult;
};

export async function saveWaOrderToPending(
  header: DemPoExtractHeader,
  rows: DemPoReviewRow[],
  createdBy: string,
  queueMessageIds: string[] = [],
  processingSeconds?: number | null,
): Promise<SaveWaOrderResult> {
  assertDemActorReady(createdBy);
  const items = rowsToSavePayload(rows);
  if (items.length === 0) {
    throw new Error('No valid line items to save. Map items or mark rows as IGNORE.');
  }

  const customerCode = header.sys_customer_code?.trim() ?? '';
  if (!customerCode) {
    throw new Error('System customer code is required.');
  }

  const db = getDbSupabase('dem');
  let orderId = await getNextWaNumber();
  const dbDate = parseDeliveryDateForDb(header.delivery_date);

  const userPo = header.po_number?.trim() ?? '';
  // Empty unless the user typed a real PO no. (ignore blank / WA* placeholders).
  const refNo = userPo && !userPo.startsWith('WA') ? userPo : '';

  if (refNo) {
    const { assertOrderWithinBlanketOutstanding } = await import('./demBlanketOrders');
    await assertOrderWithinBlanketOutstanding({
      poNumber: refNo,
      lines: items,
    });
  }

  const orderPayload: Record<string, unknown> = {
    order_id: orderId,
    ref_no: refNo,
    customer_code: customerCode,
    status: 'Pending',
    source: 'WhatsApp',
    created_by: createdBy,
  };
  if (dbDate) orderPayload.delivery_date = dbDate;
  if (processingSeconds != null && processingSeconds > 0) {
    orderPayload.processing_seconds = Math.floor(processingSeconds);
  }

  const maxRetries = 3;
  let inserted = false;

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const { error } = await db.from('orders').insert(orderPayload);
    if (!error) {
      inserted = true;
      break;
    }

    const errStr = formatSupabaseError(error).toLowerCase();
    if (errStr.includes('23505') || errStr.includes('duplicate')) {
      if (attempt === maxRetries - 1) {
        throw new Error(`Duplicate WA order clash for ${orderId}. Please try again.`);
      }
      orderId = await getNextWaNumber();
      orderPayload.order_id = orderId;
      if (!userPo || userPo.startsWith('WA')) {
        orderPayload.ref_no = '';
      }
      continue;
    }

    if (attempt === maxRetries - 1) throw new Error(formatSupabaseError(error));
    await sleep(1500);
  }

  if (!inserted) {
    throw new Error('Failed to insert WhatsApp order header.');
  }

  const itemsPayload = items.map((item) => ({
    order_id: orderId,
    item_code: item.item_code,
    description: item.description,
    qty: item.qty,
    uom: item.uom,
  }));

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    if (attempt > 0) {
      await db.from('order_items').delete().eq('order_id', orderId);
    }
    const { error } = await db.from('order_items').insert(itemsPayload);
    if (!error) break;
    if (attempt === maxRetries - 1) throw new Error(formatSupabaseError(error));
    await sleep(1500);
  }

  if (queueMessageIds.length > 0) {
    await markWhatsAppQueueProcessed(queueMessageIds);
  }

  const deliveryDisplay = dbDate
    ? parseDeliveryDateDisplay(dbDate)
    : header.delivery_date?.trim() || '—';

  const notifications = await sendPendingOrderNotifications([
    {
      order_id: orderId,
      customer_code: customerCode,
      customer_name: header.sys_customer_name?.trim() || customerCode,
      ref_no: refNo,
      delivery_date_display: deliveryDisplay,
      email: '',
      items,
    },
  ]);

  return { order_id: orderId, notifications };
}
